
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import ConnectionEditDialog from '@/components/ConnectionEditDialog.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import Editor from '@/components/Editor.vue';
import UserGroups from '@/components/UserGroups.vue';
import {
  Connection, Lead, User, AssignmentType, AssignmentAssignee,
} from '@/types';
import { currentUserHasPermission, getLeadCustomValueText, getAssignmentLabel } from '@/helpers';

moment.locale('it');

const typeConfig: Record<string, { title: string; statusLabel: string; assigneeLabel: string; prefix: string; userGroupIds: number[] }> = {
  practice: {
    title: 'Assegnatario delle pratiche di connessione',
    statusLabel: "Stato dell'assegnazione delle pratiche",
    assigneeLabel: 'Assegnatario delle pratiche di connessione',
    prefix: 'practice',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  project: {
    title: 'Assegnatario del progetto',
    statusLabel: "Stato dell'assegnazione del progetto",
    assigneeLabel: 'Assegnatario del progetto',
    prefix: 'project',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  enea: {
    title: 'Assegnatario ENEA/GSE/Altro',
    statusLabel: "Stato dell'assegnazione ENEA/GSE/Altro",
    assigneeLabel: 'Assegnatario ENEA/GSE/Altro',
    prefix: 'enea',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  dogane: {
    title: 'Assegnatario Dogane',
    statusLabel: "Stato dell'assegnazione Dogane",
    assigneeLabel: 'Assegnatario Dogane',
    prefix: 'dogane',
    // Gruppo 19 = "Progettisti/Professionisti Syndesia" (vedi backend models/assignment.js)
    userGroupIds: [19],
  },
  installer: {
    title: 'Assegnatario installatore',
    statusLabel: "Stato dell'assegnazione installatore",
    assigneeLabel: 'Installatore',
    prefix: 'installer',
    // Gruppo 1 = "Installatori Stone Pine" (vedi backend models/assignment.js)
    userGroupIds: [1],
  },
};

interface MultiAssigneeEntry {
  assigneeId: number | null;
  status: 'todo' | 'done';
  assignedDate: Date | string | null;
}

@Component({
  components: {
    ConnectionEditDialog,
    DatePickerDialog,
    Editor,
    UserGroups,
  },
})
export default class ConnectionAssignmentsDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop({ type: String, default: 'practice' }) readonly assignmentType!: string;

  dialog = false

  datePickerDialog = false
  datePickerDate: string | null = null
  datePickerTargetIndex: number | null = null

  // Singolo assegnatario (modalità legacy)
  assigneeId: number | null = null
  assignedStatus = 'todo'
  assignedDate: Date | null = null
  selectedStatus: 'completed' | 'error' = 'completed'

  // Multi-assegnatario
  assigneesList: MultiAssigneeEntry[] = []
  isNa = false

  // Note
  notesValue = ''
  notesEditorKey = 0

  assignedValues = [
    { text: 'Da assegnare', value: 'todo' },
    { text: 'Assegnate', value: 'done' },
    { text: 'Non applicabile', value: 'na' },
  ]

  // Stessa terminologia dei tipi a singolo assegnatario: per ogni installatore
  // "Da assegnare" / "Assegnato" (lo stato "Non applicabile" è a livello di
  // incarico, gestito dallo switch dedicato).
  multiAssignedValues = [
    { text: 'Da assegnare', value: 'todo' },
    { text: 'Assegnato', value: 'done' },
  ]

  get config() {
    return typeConfig[this.assignmentType] || typeConfig.practice;
  }

  get assignmentTypeConfig(): AssignmentType | undefined {
    return this.$store.getters.getAssignmentType(this.assignmentType);
  }

  get isMultiple(): boolean {
    return this.assignmentTypeConfig?.multipleAssignees === true;
  }

  get dialogTitle(): string {
    const cfg = this.assignmentTypeConfig;
    return cfg ? getAssignmentLabel(cfg, 'elettrica-figlia') : this.config.title;
  }

  get statusLabel(): string {
    const cfg = this.assignmentTypeConfig;
    return cfg
      ? `Stato dell'assegnazione: ${getAssignmentLabel(cfg, 'elettrica-figlia')}`
      : this.config.statusLabel;
  }

  get assigneeLabel(): string {
    const cfg = this.assignmentTypeConfig;
    return cfg ? getAssignmentLabel(cfg, 'elettrica-figlia') : this.config.assigneeLabel;
  }
  get prefix(): string { return this.config.prefix; }

  get notesCustomValueName(): string | null {
    const config = this.assignmentTypeConfig;
    if (!config?.notesCustomValues) return null;
    // Le connection rappresentano sempre padre + elettrica-figlia
    return config.notesCustomValues['elettrica-figlia'] ?? null;
  }

  get notesLabel(): string {
    const config = this.assignmentTypeConfig;
    return config?.notesLabels?.['elettrica-figlia'] ?? 'Note';
  }

  get notesPermission(): { show: string; edit: string } | null {
    const config = this.assignmentTypeConfig;
    if (!config?.notesPermissions) return null;
    return config.notesPermissions['elettrica-figlia'] ?? null;
  }

  get canShowNotes(): boolean {
    return !!this.notesCustomValueName && !!this.notesPermission
      && currentUserHasPermission(this.notesPermission.show);
  }

  get canEditNotes(): boolean {
    return !!this.notesPermission
      && currentUserHasPermission(this.notesPermission.edit);
  }

  get connection(): Connection | null {
    return this.$store.getters.currentConnection as Connection | null;
  }

  get lead(): Lead | null {
    return this.$store.getters.currentLead as Lead | null;
  }

  get loading(): boolean {
    return this.$store.getters.isConnectionLoading;
  }

  get updating(): boolean {
    return this.$store.getters.isConnectionUpdating;
  }

  get dirty(): boolean {
    return this.$store.getters.isConnectionDirty;
  }

  get assigneeUsers(): User[] {
    const groupIds = this.assignmentTypeConfig?.userGroupIds || this.config.userGroupIds;
    return this.$store.getters.users
      .filter((u: User) => {
        const userGroups = u.groups.map((g) => g.id);
        return groupIds.some((gid) => userGroups.includes(gid));
      });
  }

  refresh() {
    if (!this.connection) return;

    const p = this.prefix;
    this.isNa = false;

    if (this.isMultiple) {
      // Multi-assegnatario: legge dall'array. Lo stato NA in lettura è
      // denormalizzato come `${p}Assigned === 'na'` (con array vuoto).
      const flatAssigned = (this.connection as any)[`${p}Assigned`];
      const assigneesRaw = (this.connection as any)[`${p}Assignees`];
      if (flatAssigned === 'na' || assigneesRaw === 'na') {
        this.assigneesList = [];
        this.isNa = true;
      } else {
        const assignees = assigneesRaw || [];
        const parsed: AssignmentAssignee[] = typeof assignees === 'string' ? JSON.parse(assignees) : assignees;
        this.assigneesList = parsed.map((a: AssignmentAssignee) => ({
          assigneeId: a.assigneeId,
          status: a.status || 'todo',
          assignedDate: a.assignedDate,
        }));
      }
    } else {
      // Singolo assegnatario
      this.assigneeId = (this.connection as any)[`${p}AssigneeId`] ?? null;
      this.assignedStatus = (this.connection as any)[`${p}Assigned`] ?? 'todo';
      this.assignedDate = (this.connection as any)[`${p}AssignedDate`] ?? null;
    }

    // Carica le note se configurate e l'utente ha il permesso di visualizzazione
    const cvName = this.notesCustomValueName;
    if (cvName && this.canShowNotes) {
      this.notesValue = getLeadCustomValueText(this.lead, cvName) ?? '';
    } else {
      this.notesValue = '';
    }
    // Forza la ricreazione dell'Editor (il watcher su value non aggiorna in modalità simple/editing)
    // eslint-disable-next-line no-plusplus
    this.notesEditorKey++;
  }

  // --- Metodi singolo assegnatario (invariati) ---

  onFieldUpdated(field: string) {
    const update = {} as { [key: string]: unknown };
    const p = this.prefix;

    switch (field) {
      case 'assignee':
        update[`${p}Assignee`] = this.assigneeId ?? null;
        update[`${p}AssigneeId`] = this.assigneeId ?? null;
        update[`${p}AssigneeName`] = this.assigneeId
          ? (this.assigneeUsers.find((u) => u.id === this.assigneeId)?.name ?? null)
          : null;
        if (this.assigneeId === null || this.assigneeId === undefined) {
          this.assignedStatus = 'todo';
          update[`${p}Assigned`] = 'todo';
          this.assignedDate = null;
          update[`${p}AssignedDate`] = null;
        }
        break;

      case 'assigned':
        // La data non viene precompilata su 'done' (issue #1169): la inserisce l'utente.
        // Sugli altri stati viene azzerata, coerentemente col campo che si disabilita.
        if (this.assignedStatus !== 'done') {
          this.assignedDate = null;
        }
        if (this.assignedStatus === 'na') {
          this.assigneeId = null;
          update[`${p}Assignee`] = null;
          update[`${p}AssigneeId`] = null;
          update[`${p}AssigneeName`] = null;
          this.assignedDate = null;
        }
        // La data accompagna sempre lo stato: omettendola, le route la
        // ripescherebbero dall'Assignment corrente sovrascrivendo la scelta.
        update[`${p}AssignedDate`] = this.assignedDate;
        update[`${p}Assigned`] = this.assignedStatus;
        break;

      default:
        break;
    }

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }

  // --- Metodi multi-assegnatario ---

  addAssignee() {
    this.assigneesList.push({ assigneeId: null, status: 'todo', assignedDate: null });
    this.dispatchMultiUpdate();
  }

  removeAssignee(index: number) {
    this.assigneesList.splice(index, 1);
    this.dispatchMultiUpdate();
  }

  // Lo switch "Non applicabile" pilota isNa. All'attivazione stagia lo stato
  // NA (array assegnatari azzerato); alla disattivazione ripristina la lista
  // (vuota) come assegnatari reali.
  onNaToggle(val: boolean) {
    const p = this.prefix;
    if (val) {
      this.assigneesList = [];
      this.$store.dispatch('updateConnectionFields', {
        fields: { [`${p}Assignees`]: 'na' },
      });
    } else {
      this.dispatchMultiUpdate();
    }
  }

  onMultiFieldUpdated(index: number, field: string) {
    const entry = this.assigneesList[index];
    // Nessuna precompilazione su 'done' (issue #1169); su 'todo' la data viene
    // azzerata per allinearsi al backend, che non la persiste su stato non assegnato.
    if (field === 'status' && entry.status !== 'done') {
      entry.assignedDate = null;
    }
    if (field === 'assignee' && (entry.assigneeId === null || entry.assigneeId === undefined)) {
      entry.status = 'todo';
      entry.assignedDate = null;
    }
    this.dispatchMultiUpdate();
  }

  dispatchMultiUpdate() {
    const p = this.prefix;
    this.$store.dispatch('updateConnectionFields', {
      fields: {
        [`${p}Assignees`]: this.assigneesList.map((a) => ({
          assigneeId: a.assigneeId,
          assigneeName: this.assigneeUsers.find((u) => u.id === a.assigneeId)?.name ?? null,
          status: a.status,
          assignedDate: a.assignedDate,
        })),
      },
    });
  }

  // --- Note ---

  onNotesInput(val: string) {
    if (!this.canEditNotes) return;
    this.notesValue = val;
    if (this.notesCustomValueName) {
      this.$store.dispatch('updateConnectionFields', {
        fields: { [this.notesCustomValueName]: val },
      });
    }
  }

  // --- Metodi condivisi ---

  onClickDate() {
    this.datePickerTargetIndex = null;
    this.datePickerDate = this.assignedDate
      ? moment(this.assignedDate).toISOString(true).substring(0, 10)
      : null;
    this.datePickerDialog = true;
  }

  onClickMultiDate(index: number) {
    this.datePickerTargetIndex = index;
    const entry = this.assigneesList[index];
    this.datePickerDate = entry.assignedDate
      ? moment(entry.assignedDate).toISOString(true).substring(0, 10)
      : null;
    this.datePickerDialog = true;
  }

  onClearDate() {
    const update = {} as { [key: string]: unknown };
    this.assignedDate = null;
    update[`${this.prefix}AssignedDate`] = null;

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }

  onSelectDate(date: string | null) {
    this.datePickerDialog = false;

    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    if (this.datePickerTargetIndex !== null) {
      // Multi-assegnatario: aggiorna la riga specifica
      const entry = this.assigneesList[this.datePickerTargetIndex];
      if (entry) {
        entry.assignedDate = selectedDate;
        this.dispatchMultiUpdate();
      }
    } else {
      // Singolo assegnatario
      const update = {} as { [key: string]: unknown };
      this.assignedDate = selectedDate;
      update[`${this.prefix}AssignedDate`] = this.assignedDate;

      this.$store.dispatch('updateConnectionFields', {
        fields: update,
      });
    }
  }

  @Watch('value')
  onValueChange(val: boolean) {
    this.dialog = val;

    if (val) {
      this.refresh();
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }

  get fieldsDisabled(): boolean {
    return this.lead?.status !== 'open'
      || this.loading
      || this.updating;
  }
}
